import { htmlToPlainText, SANDBOX_DOMAIN } from '@/features/email-marketing/model/template';
import type { AccountCapacity, AudienceSegment, RecipientsPage } from '@/features/marketing-shared';
import type {
  EmailCampaign,
  EmailTemplate,
  RecipientRow,
  SendingIdentity,
  TemplateEditor,
} from '@/features/email-marketing/model/types';
import emailHtml from './email-html.json';

/** Destinatários como a API devolve: uma página só, com o total. */
function page<T>(rows: T[]): RecipientsPage<T> {
  return { page: 1, pageSize: 50, total: rows.length, rows };
}

export const EMPTY_AUDIENCE_ID = 'aud-vazio';
export const DEFAULT_AUDIENCE_ID = 'aud-aniversario';
export const DEFAULT_TEMPLATE_ID = 'tpl-reserva';

/** Saldo fictício da conta. Não é consulta real. 28.939 + 21.061 = 50.000. */
export const ACCOUNT_CAPACITY: AccountCapacity = {
  planName: 'Starter',
  cycleLimit: 50_000,
  sent: 28_939,
  available: 21_061,
  renewsOn: '2026-11-10T12:00:00-03:00',
  transactionalReserve: 4_000,
  companyLimit: 12_000,
  campaignLimit: 8_000,
};

export const AUDIENCES: AudienceSegment[] = [
  {
    id: 'aud-aniversario',
    name: 'Aniversariantes de novembro',
    description: 'Clientes com aniversário em novembro e e-mail utilizável.',
    total: 8_420,
    eligible: 6_420,
    excludedNoConsent: 980,
    excludedUnsubscribed: 640,
    excludedInvalid: 380,
  },
  {
    id: 'aud-ativos',
    name: 'Clientes ativos em 90 dias',
    description: 'Compraram nos últimos 90 dias. A estimativa passa do limite interno da campanha.',
    total: 14_800,
    eligible: 11_240,
    excludedNoConsent: 1_860,
    excludedUnsubscribed: 920,
    excludedInvalid: 780,
  },
  {
    id: 'aud-vip',
    name: 'Lista VIP',
    description: 'Clientes com mais de 1.000 pontos.',
    total: 420,
    eligible: 390,
    excludedNoConsent: 12,
    excludedUnsubscribed: 8,
    excludedInvalid: 10,
  },
  {
    id: EMPTY_AUDIENCE_ID,
    name: 'Importação sem e-mail válido',
    description: 'Lista recente. Ninguém pode receber esta campanha.',
    total: 2_140,
    eligible: 0,
    excludedNoConsent: 640,
    excludedUnsubscribed: 210,
    excludedInvalid: 1_290,
  },
];

function sandboxTemplate(
  template: Omit<EmailTemplate, 'origin' | 'domain' | 'tags' | 'html' | 'plainText' | 'editor' | 'beefreeJson'> & {
    editor?: TemplateEditor;
    html?: string;
    plainText?: string;
    tags?: string[];
  },
): EmailTemplate {
  return {
    editor: 'drag-drop',
    origin: 'sandbox',
    domain: SANDBOX_DOMAIN,
    tags: ['marketing', 'sandbox'],
    html: '',
    plainText: '',
    beefreeJson: '',
    ...template,
  };
}

/**
 * Biblioteca padrão. O HTML fica no campo `html` (prévia em iframe).
 * Sem documento Beefree: o editor abre em branco até alguém importar esse HTML.
 */
export const EMAIL_TEMPLATES: EmailTemplate[] = [
  sandboxTemplate({
    id: 'tpl-reserva',
    name: 'Reserva confirmada',
    category: 'Relacionamento',
    description: 'Confirmação da reserva em Lisboa, código HMA4K9.',
    subject: "You're all set for Lisbon",
    previewText: 'Your Airbnb reservation is confirmed — HMA4K9',
    editor: 'html',
    blocks: [],
    html: emailHtml.reserva,
    plainText: htmlToPlainText(emailHtml.reserva),
    tags: ['html'],
    updatedAt: '2026-10-20T23:15:00-03:00',
  }),
  sandboxTemplate({
    id: 'tpl-recibo',
    name: 'Recibo da compra',
    category: 'Aviso',
    description: 'Recibo do pedido Apple em preparação.',
    subject: 'Thanks for your order',
    previewText: 'Your Apple order is being prepared',
    editor: 'html',
    blocks: [],
    html: emailHtml.recibo,
    plainText: htmlToPlainText(emailHtml.recibo),
    tags: ['html'],
    updatedAt: '2026-10-20T23:15:00-03:00',
  }),
  sandboxTemplate({
    id: 'tpl-recompensa',
    name: 'Recompensa pronta',
    category: 'Promoção',
    description: 'Recompensa de £10 no H&M Membership.',
    subject: 'Your £10 reward is ready to use',
    previewText: "You've reached Gold status in H&M Membership",
    editor: 'html',
    blocks: [],
    html: emailHtml.recompensa,
    plainText: htmlToPlainText(emailHtml.recompensa),
    tags: ['html'],
    updatedAt: '2026-10-20T23:15:00-03:00',
  }),
];

export const SENDING_IDENTITIES: SendingIdentity[] = [
  {
    id: 'id-verificado',
    domain: 'news.exemplo.com.br',
    fromName: 'Novidades',
    fromEmail: 'novidades@news.exemplo.com.br',
    status: 'verified',
    detail: 'Status fictício de domínio verificado. Esta etapa não consulta DNS e não ativa envio.',
  },
  {
    id: 'id-pendente',
    domain: 'mail.loja-nova.com.br',
    fromName: 'Loja Nova',
    fromEmail: 'oi@mail.loja-nova.com.br',
    status: 'pending',
    detail: 'Falta o registro de verificação. Nada será publicado em DNS nesta etapa.',
  },
  {
    id: 'id-falha',
    domain: 'promo.exemplo.com.br',
    fromName: 'Promoções',
    fromEmail: 'promo@promo.exemplo.com.br',
    status: 'failed',
    detail: 'A verificação fictícia falhou. O domínio continua inativo.',
  },
];

function fromTemplate(templateId: string) {
  const template = EMAIL_TEMPLATES.find((item) => item.id === templateId);
  if (!template) throw new Error(`Template ausente: ${templateId}`);
  const sender = SENDING_IDENTITIES.find((item) => item.status === 'verified') ?? SENDING_IDENTITIES[0];
  return {
    templateId: template.id,
    templateName: template.name,
    subject: template.subject,
    previewText: template.previewText,
    fromName: sender.fromName,
    fromEmail: sender.fromEmail,
    tags: template.tags.slice(0, 5),
    blocks: template.blocks.map((block) => ({ ...block })),
  };
}

const sentRecipients: RecipientRow[] = [
  {
    id: 'rc-1',
    name: 'Ana Souza',
    emailMasked: 'a•••@exemplo.com',
    status: 'clicked',
    reason: '',
    at: '2026-10-12T12:04:00-03:00',
    device: 'Celular',
  },
  {
    id: 'rc-2',
    name: 'Bruno Lima',
    emailMasked: 'b•••@exemplo.com',
    status: 'opened',
    reason: '',
    at: '2026-10-12T11:40:00-03:00',
    device: 'Computador',
  },
  {
    id: 'rc-3',
    name: 'Carla Dias',
    emailMasked: 'c•••@exemplo.com',
    status: 'delivered',
    reason: '',
    at: '2026-10-12T10:12:00-03:00',
    device: '—',
  },
  {
    id: 'rc-4',
    name: 'Diego Martins',
    emailMasked: 'd•••@exemplo.com',
    status: 'rejected',
    reason: 'Caixa de entrada inexistente',
    at: '2026-10-12T10:08:00-03:00',
    device: '—',
  },
  {
    id: 'rc-5',
    name: 'Elena Rocha',
    emailMasked: 'e•••@exemplo.com',
    status: 'unsubscribed',
    reason: 'Descadastro em um clique',
    at: '2026-10-12T15:22:00-03:00',
    device: 'Celular',
  },
  {
    id: 'rc-6',
    name: 'Fabio Nunes',
    emailMasked: 'f•••@exemplo.com',
    status: 'clicked',
    reason: '',
    at: '2026-10-12T13:18:00-03:00',
    device: 'Computador',
  },
  {
    id: 'rc-7',
    name: 'Helena Cruz',
    emailMasked: 'h•••@exemplo.com',
    status: 'opened',
    reason: '',
    at: '2026-10-12T12:55:00-03:00',
    device: 'Celular',
  },
  {
    id: 'rc-8',
    name: 'Igor Pacheco',
    emailMasked: 'i•••@exemplo.com',
    status: 'rejected',
    reason: 'Domínio do destinatário recusou a mensagem',
    at: '2026-10-12T10:09:00-03:00',
    device: '—',
  },
];

export const EMAIL_CAMPAIGNS: EmailCampaign[] = [
  {
    id: 'cmp-rascunho',
    name: 'Prévia de Black Friday',
    status: 'draft',
    ...fromTemplate('tpl-recompensa'),
    audience: {
      audienceId: 'aud-aniversario',
      audienceName: 'Aniversariantes de novembro',
      total: 8_420,
      eligible: 6_420,
      excludedNoConsent: 980,
      excludedUnsubscribed: 640,
      excludedInvalid: 380,
    },
    scheduledAt: null,
    sendMode: null,
    createdAt: '2026-10-19T18:12:00-03:00',
  },
  {
    id: 'cmp-agendada',
    name: 'Novembro de ofertas',
    status: 'scheduled',
    ...fromTemplate('tpl-recompensa'),
    audience: {
      audienceId: 'aud-vip',
      audienceName: 'Lista VIP',
      total: 420,
      eligible: 390,
      excludedNoConsent: 12,
      excludedUnsubscribed: 8,
      excludedInvalid: 10,
    },
    scheduledAt: '2026-11-03T10:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-18T14:00:00-03:00',
  },
  {
    id: 'cmp-enviando',
    name: 'Boas-vindas da semana',
    status: 'sending',
    ...fromTemplate('tpl-reserva'),
    audience: {
      audienceId: 'aud-boas-vindas',
      audienceName: 'Cadastros dos últimos 7 dias',
      total: 2_410,
      eligible: 2_100,
      excludedNoConsent: 160,
      excludedUnsubscribed: 70,
      excludedInvalid: 80,
    },
    scheduledAt: '2026-10-20T09:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-19T17:00:00-03:00',
    metrics: {
      sent: 860,
      delivered: 812,
      opened: 120,
      clicked: 18,
      rejected: 48,
      unsubscribed: 2,
    },
    evolution: [
      { label: '20 out, 9h', sent: 200, delivered: 188 },
      { label: '20 out, 10h', sent: 540, delivered: 510 },
      { label: '20 out, 11h', sent: 860, delivered: 812 },
    ],
    errors: [{ id: 'err-envio-1', code: '552', message: 'Caixa cheia', count: 48 }],
    recipients: page([
      {
        id: 'rc-s1',
        name: 'Julia Alves',
        emailMasked: 'j•••@exemplo.com',
        status: 'delivered',
        reason: '',
        at: '2026-10-20T11:02:00-03:00',
        device: '—',
      },
      {
        id: 'rc-s2',
        name: 'Kaio Benites',
        emailMasked: 'k•••@exemplo.com',
        status: 'opened',
        reason: '',
        at: '2026-10-20T11:20:00-03:00',
        device: 'Celular',
      },
      {
        id: 'rc-s3',
        name: 'Lara Mendes',
        emailMasked: 'l•••@exemplo.com',
        status: 'queued',
        reason: '',
        at: '2026-10-20T11:40:00-03:00',
        device: '—',
      },
      {
        id: 'rc-s4',
        name: 'Miguel Torres',
        emailMasked: 'm•••@exemplo.com',
        status: 'rejected',
        reason: 'Caixa cheia',
        at: '2026-10-20T10:15:00-03:00',
        device: '—',
      },
    ]),
  },
  {
    id: 'cmp-enviada',
    name: 'Aniversário de outubro',
    status: 'sent',
    ...fromTemplate('tpl-reserva'),
    audience: {
      audienceId: 'aud-outubro',
      audienceName: 'Aniversariantes de outubro',
      total: 5_450,
      eligible: 4_800,
      excludedNoConsent: 280,
      excludedUnsubscribed: 190,
      excludedInvalid: 180,
    },
    scheduledAt: '2026-10-12T09:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-10T12:00:00-03:00',
    metrics: {
      sent: 4_800,
      delivered: 4_612,
      opened: 1_688,
      clicked: 412,
      rejected: 188,
      unsubscribed: 36,
    },
    evolution: [
      { label: '12 out, 9h', sent: 1_200, delivered: 1_140 },
      { label: '12 out, 10h', sent: 2_400, delivered: 2_280 },
      { label: '12 out, 11h', sent: 3_900, delivered: 3_720 },
      { label: '12 out, 12h', sent: 4_800, delivered: 4_612 },
    ],
    errors: [
      { id: 'err-1', code: '550', message: 'Caixa de entrada inexistente', count: 96 },
      { id: 'err-2', code: '552', message: 'Caixa cheia', count: 54 },
      { id: 'err-3', code: '554', message: 'Domínio recusou a mensagem', count: 38 },
    ],
    recipients: page(sentRecipients),
  },
  {
    id: 'cmp-pausada',
    name: 'Reativação de setembro',
    status: 'paused',
    pausedFrom: 'scheduled',
    ...fromTemplate('tpl-recibo'),
    audience: {
      audienceId: 'aud-reativacao',
      audienceName: 'Sem compra há 60 dias',
      total: 3_860,
      eligible: 3_200,
      excludedNoConsent: 340,
      excludedUnsubscribed: 180,
      excludedInvalid: 140,
    },
    scheduledAt: '2026-10-25T11:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-15T10:00:00-03:00',
  },
  {
    id: 'cmp-falha',
    name: 'Cupom de terça',
    status: 'failed',
    ...fromTemplate('tpl-recompensa'),
    subject: 'Seu cupom de terça na {{loja}}',
    audience: {
      audienceId: 'aud-cupom',
      audienceName: 'Clientes com cupom ativo',
      total: 1_720,
      eligible: 1_500,
      excludedNoConsent: 90,
      excludedUnsubscribed: 70,
      excludedInvalid: 60,
    },
    scheduledAt: '2026-10-14T08:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-13T19:00:00-03:00',
    failureSummary: 'O lote foi rejeitado porque o domínio de envio não estava verificado.',
    metrics: {
      sent: 1_500,
      delivered: 40,
      opened: 0,
      clicked: 0,
      rejected: 1_460,
      unsubscribed: 0,
    },
    evolution: [
      { label: '14 out, 8h', sent: 1_500, delivered: 40 },
    ],
    errors: [
      { id: 'err-f1', code: '550', message: 'Domínio de envio sem verificação', count: 980 },
      { id: 'err-f2', code: '550', message: 'Caixa de entrada inexistente', count: 480 },
    ],
    recipients: page([
      {
        id: 'rc-f1',
        name: 'Nina Barbosa',
        emailMasked: 'n•••@exemplo.com',
        status: 'rejected',
        reason: 'Domínio de envio sem verificação',
        at: '2026-10-14T08:06:00-03:00',
        device: '—',
      },
      {
        id: 'rc-f2',
        name: 'Otávio Reis',
        emailMasked: 'o•••@exemplo.com',
        status: 'rejected',
        reason: 'Caixa de entrada inexistente',
        at: '2026-10-14T08:07:00-03:00',
        device: '—',
      },
      {
        id: 'rc-f3',
        name: 'Paula Vieira',
        emailMasked: 'p•••@exemplo.com',
        status: 'delivered',
        reason: '',
        at: '2026-10-14T08:09:00-03:00',
        device: '—',
      },
    ]),
  },
  {
    id: 'cmp-cancelada',
    name: 'Teste interno de assunto',
    status: 'cancelled',
    ...fromTemplate('tpl-recibo'),
    audience: {
      audienceId: 'aud-teste',
      audienceName: 'Lista de teste',
      total: 96,
      eligible: 80,
      excludedNoConsent: 8,
      excludedUnsubscribed: 4,
      excludedInvalid: 4,
    },
    scheduledAt: '2026-10-09T15:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-09T14:20:00-03:00',
  },
];
